/* eslint-disable react-hooks/exhaustive-deps */
import { format, isValid, parseISO } from "date-fns";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CacheInvalidationAction } from "$utils/cache";
import { CompanyServices } from "$services";
import { getEdbApprovalInfo } from "$services/EDB.service";
import useURLQuery from "$hooks/useURLQuery";
import useAuth from "$hooks/useAuth";
import useProfileEDBExternals from "./useProfileEDBExternals";
import useProfileEDBHandlers from "./useProfileEDBHandlers";
import useProfileEDBState from "./useProfileEDBState";
import { PreparationOrderStatus } from "$types";

interface UseProfileEDBLogicProps {
  idEdb: string | null;
}

const formatEdbDate = (date?: string | null) => {
  if (!date) return null;
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : null;
};

export const useProfileEDBLogic = ({ idEdb }: UseProfileEDBLogicProps) => {
  const navigate = useNavigate();
  const urlQuery = useURLQuery();
  const { isAdmin } = useAuth();

  const formMethods = useForm<any>({
    mode: "onChange",
    defaultValues: {
      id: null,
      company: null,
      products: [],
      dispositionAdress: "",
      dispositionAdress2: "",
      dispositionCity: "",
      dispositionCountry: "France",
      dispositionDate: null,
      dispositionDateIsExact: true,
      scheduledInterventionDate: null,
      dispositionPostalCode: "",
      dispositionType: "",
      dispositionPlace: "",
      emailContactRecovery: "",
      entrepotName: "",
      integratedOrders: null,
      firstnameContactRecovery: "",
      lastnameContactRecovery: "",
      phoneContactRecovery: "",
      preparationType: "Préparation pour mise à disposition",
      analyticCode: "",
    },
  });

  const externals = useProfileEDBExternals({ idEdb });

  const {
    edb,
    formOpen,
    produit,
    setProduit,
    fetchIntegratedOrders,
    opList,
    fetchedIntegratedOrders,
    setProductPopin,
    setFormOpen,
    fetchEdb,
    updateEDBProducts,
  } = externals;

  const state = useProfileEDBState();
  const handlers = useProfileEDBHandlers({
    edb,
    integratedOrders: state.integratedOrders,
    notEditableProducts: state.notEditableProducts,
    opList,
    setProductPopin,
    setFormOpen,
    setProduit,
    fetchEdb,
    fetchIntegratedOrders,
    formMethods,
    updateEDBProducts,
    idEdb,
    produit,
    setShowCart: state.setShowCart,
    setOpenCartModalValidate: state.setOpenCartModalValidate,
    setIsAddingMaterial: state.setIsAddingMaterial,
    setCurrentTab: state.setCurrentTab,
    setProductToDeleteList: state.setProductToDeleteList,
    setOpenModalDeleteMultipleProduct: state.setOpenModalDeleteMultipleProduct,
    setIsModifyingEdb: state.setIsModifyingEdb,
    productsQuantityMap: state.productsQuantityMap,
    setProductsQuantityMap: state.setProductsQuantityMap,
  });

  const { integratedOrders, setIntegratedOrders, setNotEditableProducts } =
    state;

  const { data: edbCompanyCreatedBy } = useQuery({
    queryKey: [
      CacheInvalidationAction.GET_COMPANY_BY_ID,
      edb?.createdBy?.company,
    ],
    queryFn: () => CompanyServices.getCompanyById(edb?.createdBy?.company),
    enabled: !!edb?.createdBy?.company,
    staleTime: 60 * 1000,
  });

  const { data: approvalInfo } = useQuery({
    queryKey: ["edb-approval", idEdb],
    queryFn: () => getEdbApprovalInfo(idEdb),
    enabled: !!idEdb && !!edb,
    staleTime: 0,
  });

  const canApprove = approvalInfo?.canApprove ?? false;

  // In the Bredcrumbs file which manage the permissions to access some pages,
  // it's not possible to block a page based on a url query param: so it's done here.
  // TODO: when autorization & roles are reworked, handle this elsewhere
  useEffect(() => {
    if (!isAdmin() && parseInt(urlQuery.get("tabpane"), 10) === 3) {
      navigate("/forbidden");
    }
  }, [navigate, isAdmin, urlQuery]);

  useEffect(() => {
    if (urlQuery.get("approval") === "true" && canApprove && !edb?.isDisable) {
      state.setIsApprovalModalOpen(true);
    }
  }, [canApprove, edb?.isDisable, urlQuery]);

  // handle form hc
  useEffect(() => {
    if (!formOpen) {
      setProduit(null);
    }
  }, [formOpen, produit, setProduit]);

  useEffect(() => {
    if (edb) {
      const defaultFormValues = {
        ...edb,
        company: edb.company._id,
        dockyard: edb.dockyard?._id,
        project: edb.project._id,
        products: formMethods.getValues("products") || [],
        dispositionDate: formatEdbDate(edb.dispositionDate),
        scheduledInterventionDate: formatEdbDate(edb.scheduledInterventionDate),
      };
      formMethods.reset(defaultFormValues);
    }
  }, [edb, formMethods.reset]);

  useEffect(() => {
    if (edb) {
      fetchIntegratedOrders(edb._id);
    }
  }, [fetchIntegratedOrders, edb]);

  useEffect(() => {
    if (fetchedIntegratedOrders) {
      setIntegratedOrders(fetchedIntegratedOrders);
    }
  }, [fetchedIntegratedOrders]);

  useEffect(() => {
    if (edb) {
      const defaultFormValues = {
        ...edb,
        integratedOrders: {
          ...integratedOrders,
          orderProducts: [],
        },
        company: edb.company._id,
        dockyard: edb.dockyard?._id,
        project: edb.project._id,
        products: formMethods.getValues("products") || [],
        dispositionDate: formatEdbDate(edb.dispositionDate),
        scheduledInterventionDate: formatEdbDate(edb.scheduledInterventionDate),
      };

      formMethods.reset(defaultFormValues);
    }
  }, [edb, formMethods.reset, integratedOrders]);

  useEffect(() => {
    const tmpNotEditableProduct = [];
    if (opList) {
      opList.forEach((op: any) => {
        if (op.status !== PreparationOrderStatus.CANCELLED) {
          op.products.forEach((opProduct: any) => {
            tmpNotEditableProduct.push({
              productId: opProduct.relatedProduct._id,
              qty: opProduct.qty,
            });
          });
        }
      });
      setNotEditableProducts(tmpNotEditableProduct);
    }
  }, [opList]);

  const value = useMemo(() => {
    return {
      idEdb,
      ...state,
      ...handlers,
      ...externals,
      edbCompanyCreatedBy,
      canApprove,
      formMethods,
    };
  }, [
    idEdb,
    state,
    handlers,
    externals,
    edbCompanyCreatedBy,
    canApprove,
    formMethods,
  ]);

  return value;
};
